/**
 * Scoped voice browser storage (V4-LANGUAGE-STORAGE).
 * All keys are namespaced `voice:<userId>:<businessId>:<suffix>`.
 * Legacy pending financial / mutation state is never migrated as executable.
 */

import {
  VOICE_PENDING_TTL,
  VOICE_STORAGE_SUFFIXES,
  buildVoiceStorageKey,
  type VoiceStorageSuffix,
} from './constants';

export interface VoiceStorageScope {
  userId: string;
  businessId: string;
}

interface StoredEnvelope<T> {
  v: T;
  schema: 1;
  createdAt: number;
  expiresAt?: number;
}

const MIGRATION_MARKER_SUFFIX = 'migration' as const;
const LEGACY_MIGRATION_VERSION = 1;

/** Legacy flat keys that may exist from pre-V4 clients. */
export const LEGACY_VOICE_KEYS = {
  settings: 'voiceSettings',
  settingsAlt: 'voice_settings',
  sessions: 'voiceAssistantSessions',
  currentSession: 'voiceAssistantCurrentSessionId',
  history: 'voiceAssistantHistory',
  backendSession: 'voiceSessionId',
  uiSession: 'voiceUiSessionId',
  pendingCommandId: 'voicePendingCommandId',
  pendingContactConfirmation: 'voicePendingContactConfirmation',
  pendingContactDisambiguation: 'voicePendingContactDisambiguation',
  pendingDocumentFlow: 'voicePendingDocumentFlow',
} as const;

/** Executable / financial pending state — discard on migration, never promote. */
const LEGACY_EXECUTABLE_PENDING_KEYS = [
  LEGACY_VOICE_KEYS.pendingCommandId,
  LEGACY_VOICE_KEYS.pendingContactConfirmation,
  LEGACY_VOICE_KEYS.pendingContactDisambiguation,
  LEGACY_VOICE_KEYS.pendingDocumentFlow,
] as const;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function isExpired(
  record: { expiresAt?: number | string | null } | null | undefined,
  now = Date.now()
): boolean {
  if (!record?.expiresAt) return false;
  const expiresAt =
    typeof record.expiresAt === 'string'
      ? Date.parse(record.expiresAt)
      : record.expiresAt;
  if (!Number.isFinite(expiresAt)) return false;
  return now >= expiresAt;
}

function wrapValue<T>(value: T, ttlMs?: number): StoredEnvelope<T> {
  const createdAt = Date.now();
  const envelope: StoredEnvelope<T> = { v: value, schema: 1, createdAt };
  if (ttlMs != null && ttlMs > 0) {
    envelope.expiresAt = createdAt + ttlMs;
  }
  return envelope;
}

function unwrapValue<T>(
  raw: string | null,
  key: string
): T | null {
  const parsed = safeParse<StoredEnvelope<T> | T>(raw);
  if (parsed == null) return null;

  // New envelope shape
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'schema' in parsed &&
    (parsed as StoredEnvelope<T>).schema === 1 &&
    'v' in parsed
  ) {
    const envelope = parsed as StoredEnvelope<T>;
    if (isExpired(envelope)) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      return null;
    }
    return envelope.v;
  }

  // Raw legacy-shaped value written under a scoped key (treat as valid, no TTL)
  return parsed as T;
}

export function readVoiceKey<T>(
  scope: VoiceStorageScope,
  suffix: VoiceStorageSuffix
): T | null {
  if (!scope.userId || !scope.businessId) return null;
  if (typeof localStorage === 'undefined') return null;
  const key = buildVoiceStorageKey(scope.userId, scope.businessId, suffix);
  return unwrapValue<T>(localStorage.getItem(key), key);
}

export function writeVoiceKey<T>(
  scope: VoiceStorageScope,
  suffix: VoiceStorageSuffix,
  value: T,
  ttlMs?: number
): void {
  if (!scope.userId || !scope.businessId) return;
  if (typeof localStorage === 'undefined') return;
  const key = buildVoiceStorageKey(scope.userId, scope.businessId, suffix);
  const defaultTtl =
    suffix === VOICE_STORAGE_SUFFIXES.pendingAction ||
    suffix === VOICE_STORAGE_SUFFIXES.pendingDocument
      ? VOICE_PENDING_TTL.DEFAULT_MS
      : suffix === VOICE_STORAGE_SUFFIXES.backendSession
        ? VOICE_PENDING_TTL.MAX_MS
        : undefined;
  const envelope = wrapValue(value, ttlMs ?? defaultTtl);
  localStorage.setItem(key, JSON.stringify(envelope));
}

export function removeVoiceKey(
  scope: VoiceStorageScope,
  suffix: VoiceStorageSuffix
): void {
  if (!scope.userId || !scope.businessId) return;
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(
    buildVoiceStorageKey(scope.userId, scope.businessId, suffix)
  );
}

/** Remove every `voice:<userId>:<businessId>:*` key for the scope. */
export function clearVoiceScope(scope: VoiceStorageScope): void {
  if (!scope.userId || !scope.businessId) return;
  if (typeof localStorage === 'undefined') return;
  const prefix = `voice:${scope.userId}:${scope.businessId}:`;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) toRemove.push(key);
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
}

function migrationMarkerKey(scope: VoiceStorageScope): string {
  return `voice:${scope.userId}:${scope.businessId}:${MIGRATION_MARKER_SUFFIX}`;
}

/**
 * One-way safe migration from flat legacy keys into the scoped namespace.
 * Preferential / history data may migrate; executable pending mutations are discarded.
 */
export function migrateLegacyVoiceStorage(scope: VoiceStorageScope): void {
  if (!scope.userId || !scope.businessId) return;
  if (typeof localStorage === 'undefined') return;

  const markerKey = migrationMarkerKey(scope);
  const existing = safeParse<{ v?: number }>(localStorage.getItem(markerKey));
  if (existing?.v === LEGACY_MIGRATION_VERSION) return;

  // Settings — harmless preferences
  const settingsRaw = localStorage.getItem(LEGACY_VOICE_KEYS.settings);
  if (settingsRaw && !readVoiceKey(scope, VOICE_STORAGE_SUFFIXES.settings)) {
    const parsed = safeParse(settingsRaw);
    if (parsed) {
      writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.settings, parsed);
    }
  }

  // Sessions / history — not executable
  const sessionsRaw = localStorage.getItem(LEGACY_VOICE_KEYS.sessions);
  if (sessionsRaw && !readVoiceKey(scope, VOICE_STORAGE_SUFFIXES.sessions)) {
    const parsed = safeParse(sessionsRaw);
    if (parsed) {
      writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.sessions, parsed);
    }
  }

  const currentSessionId = localStorage.getItem(LEGACY_VOICE_KEYS.currentSession);
  if (
    currentSessionId &&
    !readVoiceKey(scope, VOICE_STORAGE_SUFFIXES.activeSession)
  ) {
    writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.activeSession, currentSessionId);
  }

  // Backend session — migrate with short TTL so stale IDs expire
  const backendSession = localStorage.getItem(LEGACY_VOICE_KEYS.backendSession);
  if (
    backendSession &&
    !readVoiceKey(scope, VOICE_STORAGE_SUFFIXES.backendSession)
  ) {
    writeVoiceKey(
      scope,
      VOICE_STORAGE_SUFFIXES.backendSession,
      backendSession,
      VOICE_PENDING_TTL.DEFAULT_MS
    );
  }

  // Discard executable pending financial / contact / document state
  for (const key of LEGACY_EXECUTABLE_PENDING_KEYS) {
    localStorage.removeItem(key);
  }

  // Do not migrate ui session — rotate fresh is safer
  localStorage.removeItem(LEGACY_VOICE_KEYS.uiSession);

  // Clear migrated / obsolete legacy keys
  localStorage.removeItem(LEGACY_VOICE_KEYS.settings);
  localStorage.removeItem(LEGACY_VOICE_KEYS.settingsAlt);
  localStorage.removeItem(LEGACY_VOICE_KEYS.sessions);
  localStorage.removeItem(LEGACY_VOICE_KEYS.currentSession);
  localStorage.removeItem(LEGACY_VOICE_KEYS.history);
  localStorage.removeItem(LEGACY_VOICE_KEYS.backendSession);

  localStorage.setItem(
    markerKey,
    JSON.stringify({ v: LEGACY_MIGRATION_VERSION, migratedAt: Date.now() })
  );
}

/**
 * Resolve active auth scope from localStorage / optional overrides.
 * Returns null when user or business is unknown (storage stays inert).
 */
export function resolveVoiceStorageScope(overrides?: {
  userId?: string | number | null;
  businessId?: string | number | null;
}): VoiceStorageScope | null {
  if (typeof localStorage === 'undefined') return null;

  let userId =
    overrides?.userId != null && overrides.userId !== ''
      ? String(overrides.userId)
      : '';
  let businessId =
    overrides?.businessId != null && overrides.businessId !== ''
      ? String(overrides.businessId)
      : localStorage.getItem('businessId') || '';

  if (!userId) {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw) as { id?: string | number };
        if (parsed?.id != null) userId = String(parsed.id);
      }
    } catch {
      /* ignore */
    }
  }

  if (!userId || !businessId) return null;
  return { userId, businessId };
}

/** Clear operational pending suffixes without wiping settings/history. */
export function clearVoiceOperationalState(scope: VoiceStorageScope): void {
  removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingAction);
  removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingDocument);
  removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.backendSession);
  // Also clear any leftover legacy executable keys (cross-tab safety)
  if (typeof localStorage !== 'undefined') {
    for (const key of LEGACY_EXECUTABLE_PENDING_KEYS) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem(LEGACY_VOICE_KEYS.pendingCommandId);
    localStorage.removeItem(LEGACY_VOICE_KEYS.backendSession);
  }
}
